import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEscClose } from "./filters";

/**
 * Command palette (Phase 10 — Safe Zap v2.0).
 * Cmd/Ctrl+K para abrir. Busca fuzzy básica + navegação por teclado.
 *
 * commands: [{ id, label, hint?, group?, keywords?, icon?, run() }]
 */
export function CommandPalette({ open, onClose, commands = [], placeholder = "Busque uma página, ação ou atalho..." }) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEscClose(open, onClose);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      // próximo tick pra garantir foco após render
      const id = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    const tokens = q.split(/\s+/).filter(Boolean);
    return commands
      .map((cmd) => {
        const haystack = [
          cmd.label,
          cmd.hint || "",
          cmd.group || "",
          ...(cmd.keywords || []),
        ].join(" ").toLowerCase();
        const match = tokens.every((t) => haystack.includes(t));
        if (!match) return null;
        // simple score: prefer matches in label
        let score = 0;
        for (const t of tokens) {
          if ((cmd.label || "").toLowerCase().includes(t)) score += 3;
          if ((cmd.hint || "").toLowerCase().includes(t)) score += 1;
        }
        return { cmd, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.cmd);
  }, [commands, query]);

  // Group results by .group (preserves first-seen order)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const cmd of results) {
      const key = cmd.group || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(cmd);
    }
    return Array.from(map.entries());
  }, [results]);

  // Flat ordered list for keyboard navigation
  const flat = useMemo(() => grouped.flatMap(([, items]) => items), [grouped]);

  // Reset highlight when results shrink
  useEffect(() => {
    if (highlight >= flat.length) setHighlight(0);
  }, [flat.length, highlight]);

  // Scroll highlighted into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = flat[highlight];
      if (cmd) {
        try { cmd.run?.(); } finally { onClose?.(); }
      }
    }
  }

  if (!open) return null;

  return (
    <div className="uiCmdPalette" role="dialog" aria-modal="true" aria-label="Paleta de comandos">
      <div className="uiCmdPalette-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="uiCmdPalette-box" onKeyDown={onKeyDown}>
        <div className="uiCmdPalette-input">
          <span className="uiCmdPalette-icon" aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck="false"
          />
          <span className="uiCmdPalette-esc">Esc</span>
        </div>

        <div className="uiCmdPalette-list" ref={listRef}>
          {flat.length === 0 ? (
            <div className="uiCmdPalette-empty">
              Nenhum resultado para "{query}".
            </div>
          ) : (
            grouped.map(([group, items]) => (
              <div key={group || "_"} className="uiCmdPalette-group">
                {group && <div className="uiCmdPalette-groupTitle">{group}</div>}
                {items.map((cmd) => {
                  const idx = flat.indexOf(cmd);
                  const active = idx === highlight;
                  return (
                    <button
                      type="button"
                      key={cmd.id}
                      data-cmd-idx={idx}
                      className={`uiCmdPalette-item${active ? " active" : ""}`}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => { try { cmd.run?.(); } finally { onClose?.(); } }}
                    >
                      {cmd.icon && <span className="uiCmdPalette-itemIcon">{cmd.icon}</span>}
                      <div className="uiCmdPalette-itemBody">
                        <span className="uiCmdPalette-itemLabel">{cmd.label}</span>
                        {cmd.hint && <span className="uiCmdPalette-itemHint">{cmd.hint}</span>}
                      </div>
                      {cmd.shortcut && <kbd className="uiCmdPalette-kbd">{cmd.shortcut}</kbd>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="uiCmdPalette-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
          <span><kbd>↵</kbd> abrir</span>
          <span><kbd>Esc</kbd> fechar</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook que registra Cmd/Ctrl+K globalmente para abrir o palette.
 */
export function useCommandHotkey(setOpen) {
  useEffect(() => {
    function handler(e) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [setOpen]);
}
